import { useCallback, useState, type MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { addressLine, money, wholeDollars } from '../../lib/format.js'
import { useJobHref } from '../../lib/job-overlay.js'
import type { RouterOutputs } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { andList, dayLabel, everyLabel } from '../repeat/rule.js'
import {
  MARK_LABEL,
  ServiceTracks,
  SlotDot,
  menuPoint,
  type Mark,
  type TrackGroup,
  type TrackRow,
} from '../repeat/ServiceTracks.js'
import { VisitMenu, serviceName, type VisitMenuTarget } from './VisitMenu.js'

export type AgreementPage = RouterOutputs['agreements']['byId']
type Row = AgreementPage['schedules'][number]
type Booking = AgreementPage['bookings'][number]

/** The page grouped by site, each with its services; or by service, each with its sites. */
export type GridBy = 'site' | 'service'

/** "Every 3 months · $200.00 · stopped", under a service's name. */
export function trackSub(
  row: Pick<Row, 'intervalCount' | 'intervalUnit' | 'stoppedAt'> & { priceIncTax: number | null },
): string {
  return [
    everyLabel(row).replace(/^./, (letter) => letter.toUpperCase()),
    row.priceIncTax ? money(row.priceIncTax) : null,
    row.stoppedAt ? 'stopped' : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** A visit still to happen: made and not finished, or not made yet. */
const COMING = new Set(['CREATED', 'OVERDUE', 'NEXT', 'PLANNED'])

const siteName = (row: Pick<Row, 'site'>) => row.site?.name ?? addressLine(row.site?.address)

/**
 * What makes two schedules one service on the By service view: the same
 * services, as often, counted the same way, with the same instructions.
 * Anything different is a service of its own, even under the same name.
 */
export function serviceKey(row: Pick<Row, 'services' | 'intervalCount' | 'intervalUnit' | 'anchor' | 'standingNotes'>): string {
  return [
    row.services
      .map((service) => service.serviceTypeId)
      .sort()
      .join('+'),
    `${row.intervalCount}-${row.intervalUnit}`,
    row.anchor,
    (row.standingNotes ?? '').trim().replace(/\s+/g, ' ').toLowerCase(),
  ].join('|')
}

/** The agreement's schedules as services, each with its sites, in the order they were added. */
export function serviceGroups(schedules: readonly Row[]): Row[][] {
  const groups = new Map<string, Row[]>()
  for (const row of schedules) {
    const key = serviceKey(row)
    groups.set(key, [...(groups.get(key) ?? []), row])
  }
  return [...groups.values()]
}

/**
 * The agreement as a year on a page, each visit of the term in its month.
 * Decision 96.
 *
 * By site (the default) draws each site and the services at it; by service
 * draws each service — its cadence and instructions — and the sites it is
 * done at. Every dot is a slot the server computed with `repeatSlots`, the
 * same one the job card's timeline draws, so the grid and the tab cannot
 * disagree about when a visit falls. A dot with a job opens it.
 *
 * By site, a site with more than one service ends with a Bookings row: what
 * its visits become on the board, as the server forecasts it
 * (`planBookings`), with a count on each booking that carries more than one
 * visit.
 *
 * A right-click on any booking or visit opens `VisitMenu` there, and a
 * left-click on one with no job opens it at the mark: make the job and open
 * it, a note, move it, put it back on schedule, skip it, split it. With
 * `canEdit` false the menu only reads.
 *
 * With `onAddService` each site's heading offers a service there, and with
 * `onAddSite` the site view ends with a row that adds a site. With
 * `onAddSites` each service offers more sites, with `onEditAcross` a service
 * at more than one site can be edited at all of them at once, and with
 * `onAddServiceAcross` the service view ends with a row that adds a service. With `onEditSite` a
 * site's heading offers Edit site, and with `onEditService` a row's name
 * opens its editor.
 */
export function AgreementGrid({
  page,
  by = 'site',
  onAddService,
  onAddSite,
  onAddSites,
  onEditAcross,
  onAddServiceAcross,
  onEditSite,
  onEditService,
  canEdit = false,
}: {
  page: AgreementPage
  by?: GridBy
  onAddService?: (siteId: string) => void
  onAddSite?: () => void
  onAddSites?: (rows: readonly Row[]) => void
  onEditAcross?: (rows: readonly Row[]) => void
  onAddServiceAcross?: () => void
  onEditSite?: (siteId: string) => void
  onEditService?: (row: Row) => void
  canEdit?: boolean
}) {
  const [menu, setMenu] = useState<{ target: VisitMenuTarget; at: { x: number; y: number } } | null>(null)
  const closeMenu = useCallback(() => setMenu(null), [])
  const names = new Map(page.schedules.map((row) => [row.id, serviceName(row)]))

  /** A service's row: its visits in their months, and the next one at the end. */
  const trackRow = (row: Row, name: string, sub: string, editLabel: string): TrackRow => {
    const next = row.slots.find((slot) => COMING.has(slot.state))
    const on = next ? (next.job?.bookedOn ?? next.dueOn) : null
    return {
      id: row.id,
      name,
      sub,
      onEdit: onEditService ? () => onEditService(row) : undefined,
      editLabel,
      slots: row.slots,
      paused: row.paused,
      onSlotMenu: (slot, at) => setMenu({ target: { kind: 'visit', row, slot }, at }),
      aside: row.paused ? (
        <span className="font-normal text-ink-400">Paused</span>
      ) : on ? (
        dayLabel(on, { year: false })
      ) : (
        '—'
      ),
    }
  }

  const bySite = new Map<string, Row[]>()
  for (const row of page.schedules) {
    bySite.set(row.siteId, [...(bySite.get(row.siteId) ?? []), row])
  }

  const groups: TrackGroup[] =
    by === 'service'
      ? serviceGroups(page.schedules).map((rows) => {
          const first = rows[0]!
          const name = serviceName(first)
          const aYear = rows.reduce((sum, row) => sum + row.aYear, 0)
          const notes = first.standingNotes?.trim() ?? ''
          return {
            key: first.id,
            heading: (
              <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-[13.5px] font-semibold text-ink-900">{name}</span>
                <span className="text-xs text-ink-500">
                  {[
                    everyLabel(first).replace(/^./, (letter) => letter.toUpperCase()),
                    first.anchor === 'COMPLETED_DATE' ? 'from when the last is done' : null,
                    `${rows.length} site${rows.length === 1 ? '' : 's'}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                {notes ? (
                  <span title={notes} className="max-w-[420px] min-w-0 truncate text-xs text-ink-500 italic">
                    {`“${notes}”`}
                  </span>
                ) : null}
                <span className="ml-auto flex items-baseline gap-1.5">
                  {onEditAcross && rows.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => onEditAcross(rows)}
                      aria-label={`Edit ${name}, ${everyLabel(first)}, at all ${rows.length} sites`}
                      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900"
                    >
                      <Icon name="pencil" className="size-3.5" />
                      {`Edit at all ${rows.length}`}
                    </button>
                  ) : null}
                  {onAddSites ? (
                    <button
                      type="button"
                      onClick={() => onAddSites(rows)}
                      aria-label={`Add sites to ${name}, ${everyLabel(first)}`}
                      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-brand-700 hover:bg-brand-50"
                    >
                      <Icon name="plus" className="size-3.5" />
                      Add sites
                    </button>
                  ) : null}
                  <span className="ml-1.5 text-xs text-ink-400 tabular-nums">{`${wholeDollars(aYear)} a year`}</span>
                </span>
              </span>
            ),
            rows: rows.map((row) =>
              trackRow(
                row,
                siteName(row),
                [row.priceIncTax ? `${money(row.priceIncTax)} a visit` : 'no price', row.stoppedAt ? 'paused' : null]
                  .filter(Boolean)
                  .join(' · '),
                `Edit ${name} at ${siteName(row)}`,
              ),
            ),
          }
        })
      : [...bySite.entries()].map(([siteId, rows]) => {
          const site = rows[0]!.site
          const name = site?.name ?? addressLine(site?.address)
          const aYear = rows.reduce((sum, row) => sum + row.aYear, 0)
          return {
            key: siteId,
            heading: (
              <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-[13.5px] font-semibold text-ink-900">{name}</span>
                {site?.name ? <span className="text-xs text-ink-500">{addressLine(site.address)}</span> : null}
                <span className="ml-auto flex items-baseline gap-1.5">
                  {onEditSite ? (
                    <button
                      type="button"
                      onClick={() => onEditSite(siteId)}
                      aria-label={`Edit ${name}`}
                      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900"
                    >
                      <Icon name="pencil" className="size-3.5" />
                      Edit site
                    </button>
                  ) : null}
                  {onAddService ? (
                    <button
                      type="button"
                      onClick={() => onAddService(siteId)}
                      aria-label={`Add a service at ${name}`}
                      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium text-brand-700 hover:bg-brand-50"
                    >
                      <Icon name="plus" className="size-3.5" />
                      Add service
                    </button>
                  ) : null}
                  <span className="ml-1.5 text-xs text-ink-400 tabular-nums">{`${wholeDollars(aYear)} a year`}</span>
                </span>
              </span>
            ),
            rows: rows
              .map((row): TrackRow => {
                const serviceLabel = serviceName(row)
                return trackRow(
                  row,
                  serviceLabel,
                  [
                    everyLabel(row).replace(/^./, (letter) => letter.toUpperCase()),
                    row.priceIncTax ? money(row.priceIncTax) : 'no price',
                  ].join(' · '),
                  `Edit ${serviceLabel} at ${name}`,
                )
                // What the site's visits become on the board, once there is more than one service to share a job.
              })
              .concat(rows.length > 1 ? [bookingsRow(siteId)] : []),
          }
        })

  /** The site's Bookings row: "4 for 12 visits", and the bookings in each month. */
  function bookingsRow(siteId: string): TrackRow {
    const bookings = page.bookings.filter(
      (booking) => booking.siteId === siteId && page.months.includes(booking.dueOn.slice(0, 7)),
    )
    const visits = bookings.reduce((sum, booking) => sum + booking.visits.length, 0)
    return {
      id: `${siteId}:bookings`,
      name: 'Bookings',
      sub: `${bookings.length} for ${visits} visit${visits === 1 ? '' : 's'}`,
      slots: [],
      marks: (month: string) =>
        bookings
          .filter((booking) => booking.dueOn.startsWith(month))
          .map((booking) => (
            <BookingMark
              key={booking.key}
              booking={booking}
              names={names}
              paused={page.status === 'PAUSED'}
              open={menu?.target.kind === 'booking' && menu.target.booking.key === booking.key}
              onMenu={(at) => setMenu({ target: { kind: 'booking', booking }, at })}
            />
          )),
    }
  }

  const footer =
    by === 'service' ? (
      onAddServiceAcross ? (
        <FooterButton onClick={onAddServiceAcross}>Add a service</FooterButton>
      ) : null
    ) : onAddSite ? (
      <FooterButton onClick={onAddSite}>Add a site</FooterButton>
    ) : null

  return (
    <>
      <ServiceTracks
        label="Sites and services"
        months={page.months}
        today={page.today}
        groups={groups}
        look="dots"
        firstLabel={by === 'service' ? 'Service and site' : 'Site and service'}
        showAside
        showKey={false}
        footer={footer}
      />
      {menu ? (
        <VisitMenu
          key={menu.target.kind === 'booking' ? menu.target.booking.key : `${menu.target.row.id}:${menu.target.slot.sequence}`}
          page={page}
          target={menu.target}
          position={menu.at}
          canEdit={canEdit}
          onClose={closeMenu}
          onEditService={onEditService}
          onEditSite={onEditSite}
        />
      ) : null}
    </>
  )
}

function FooterButton({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[13px] font-medium text-brand-700 hover:bg-brand-50"
    >
      <Icon name="plus" className="size-4" />
      {children}
    </button>
  )
}

/** How a booking is drawn: the mark of its visits, the soonest first. */
function bookingMark(booking: Booking, paused: boolean): Mark {
  switch (booking.state) {
    case 'DONE':
      return 'done'
    case 'CREATED':
      return 'booked'
    case 'SKIPPED':
    case 'UNSUCCESSFUL':
      return 'skipped'
    default:
      if (paused) return 'paused'
      return booking.state === 'OVERDUE' ? 'overdue' : 'planned'
  }
}

/** A booking with more than one visit on it: the dot's own look, with the count inside. */
function BookingDot({ mark, count }: { mark: Mark; count: number }) {
  if (count < 2) return <SlotDot mark={mark} look="dots" />
  const box = 'grid size-[18px] place-items-center rounded-sm text-[10px] leading-none font-bold tabular-nums'
  switch (mark) {
    case 'done':
      return <span className={cn(box, 'bg-brand-500 text-ink-900 ring-2 ring-brand-100')}>{count}</span>
    case 'current':
    case 'booked':
      return <span className={cn(box, 'bg-ink-900 text-white ring-2 ring-ink-200')}>{count}</span>
    case 'overdue':
      return <span className={cn(box, 'border-[1.5px] border-dashed border-red-600 bg-red-50 text-red-700')}>{count}</span>
    case 'skipped':
      return <span className={cn(box, 'bg-ink-200 text-ink-600')}>{count}</span>
    case 'paused':
      return <span className={cn(box, 'border-[1.5px] border-dotted border-ink-400 bg-white text-ink-500')}>{count}</span>
    default:
      return <span className={cn(box, 'border-[1.5px] border-dashed border-ink-500 bg-white text-ink-800')}>{count}</span>
  }
}

/**
 * One booking on the Bookings row. One already made is its job, and opens
 * it. One still to make opens the booking's menu; a right-click opens the
 * menu on either.
 */
function BookingMark({
  booking,
  names,
  paused,
  open,
  onMenu,
}: {
  booking: Booking
  names: ReadonlyMap<string, string>
  paused: boolean
  open: boolean
  onMenu: (at: { x: number; y: number }) => void
}) {
  const jobHref = useJobHref()
  const mark = bookingMark(booking, paused)
  const services = andList(booking.visits.map((visit) => names.get(visit.scheduleId) ?? 'Service'))
  const label = [
    `${MARK_LABEL[mark]} booking`,
    dayLabel(booking.dueOn),
    booking.split ? 'split off' : null,
    services,
  ]
    .filter(Boolean)
    .join(' · ')
  const dot = <BookingDot mark={mark} count={booking.visits.length} />
  const onContextMenu = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    onMenu(menuPoint(event))
  }

  if (booking.jobId) {
    return (
      <Link
        to={jobHref(booking.jobId)}
        title={label}
        aria-label={label}
        onContextMenu={onContextMenu}
        className={cn('relative rounded-sm hover:ring-2 hover:ring-ink-200', open && 'ring-2 ring-ink-900')}
      >
        {dot}
      </Link>
    )
  }

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-haspopup="menu"
      aria-expanded={open}
      onClick={(event) => onMenu(menuPoint(event))}
      onContextMenu={onContextMenu}
      className={cn('relative grid place-items-center rounded-sm hover:ring-2 hover:ring-ink-200', open && 'ring-2 ring-ink-900')}
    >
      {dot}
    </button>
  )
}
